/*
  Dhrohar collection/search helpers. Dawn's facets.js keeps doing all filtering, sorting, URL and
  history work; this file only:
  1. <dhrohar-scroll-memory>: remembers the scroll position of a listing URL (sessionStorage) and
     restores it when the shopper comes back with the browser Back button after a full page load.
     It ignores in-page facet history (popstate) and bfcache restores, which the browser handles.
  2. Keeps the mobile filter drawer's "Show N results" buttons in step with Dawn's #ProductCount.
  Safe to load more than once and after section reloads in the theme editor.
*/
(() => {
  if (!window.customElements || customElements.get('dhrohar-scroll-memory')) return;

  const PREFIX = 'dhrohar:scroll:';
  const keyFor = () => PREFIX + window.location.pathname + window.location.search;

  const session = {
    get(key) {
      try {
        return window.sessionStorage.getItem(key);
      } catch (e) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.sessionStorage.setItem(key, value);
      } catch (e) {
        /* storage unavailable: nothing to remember */
      }
    },
  };

  const save = () => session.set(keyFor(), String(Math.round(window.scrollY || window.pageYOffset || 0)));

  function navigationType() {
    try {
      const entry = performance.getEntriesByType('navigation')[0];
      if (entry) return entry.type;
    } catch (e) {
      /* old browser */
    }
    try {
      return performance.navigation && performance.navigation.type === 2 ? 'back_forward' : 'navigate';
    } catch (e) {
      return 'navigate';
    }
  }

  let listenersAdded = false;
  let restored = false;

  class DhroharScrollMemory extends HTMLElement {
    connectedCallback() {
      if (!listenersAdded) {
        listenersAdded = true;
        window.addEventListener('pagehide', save);
        // Save right before leaving for a product (pagehide can be late on some mobile browsers).
        document.addEventListener(
          'click',
          (event) => {
            const link = event.target.closest && event.target.closest('a[href]');
            if (link && /\/products\//.test(link.getAttribute('href') || '')) save();
          },
          true
        );
      }
      if (restored) return;
      restored = true;
      if (navigationType() !== 'back_forward') return;
      const stored = Number(session.get(keyFor()));
      if (!stored || stored < 1) return;
      const restore = () => {
        if (Math.abs((window.scrollY || 0) - stored) > 40) window.scrollTo(0, stored);
      };
      requestAnimationFrame(() => requestAnimationFrame(restore));
      // Images above the fold can shift the layout before load; try once more.
      window.addEventListener('load', restore, { once: true });
    }
  }
  customElements.define('dhrohar-scroll-memory', DhroharScrollMemory);

  /* "Show N results" buttons follow Dawn's product count after each facet update. */
  function updateApplyButtons() {
    const count = document.getElementById('ProductCount');
    if (!count) return;
    const n = parseInt(count.dataset.productCount, 10);
    if (Number.isNaN(n)) return;
    const label = `Show ${n} result${n === 1 ? '' : 's'}`;
    document.querySelectorAll('[data-dhrohar-apply]').forEach((button) => {
      if (button.textContent.trim() !== label) button.textContent = label;
    });
  }

  let observer = null;
  function watchCount() {
    const count = document.getElementById('ProductCount');
    if (!count || !window.MutationObserver) return;
    if (observer) observer.disconnect();
    observer = new MutationObserver(updateApplyButtons);
    observer.observe(count, { attributes: true, attributeFilter: ['data-product-count'], childList: true, characterData: true, subtree: true });
    updateApplyButtons();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', watchCount);
  else watchCount();
  document.addEventListener('shopify:section:load', watchCount);
})();
